import type { ExteriorFinishData, ExteriorFinishType } from '../types/exteriorFinish';

export interface ExteriorFinishPresentation {
  finishType: ExteriorFinishType;
  hasWrapOrPpf: boolean;
  badgeLabel: 'Wrap' | 'PPF' | null;
  primaryText: string;
  secondaryText?: string;
  detailText?: string;
  swatchLabel?: string;
}

export function normalizeExteriorFinishType(value?: string | null): ExteriorFinishType {
  if (value === 'wrap' || value === 'ppf') {
    return value;
  }
  return 'paint';
}

export function getExteriorFinishPresentation(data: ExteriorFinishData): ExteriorFinishPresentation | null {
  const finishType = normalizeExteriorFinishType(data.exterior_finish_type);
  const factoryColor = data.color?.trim() || '';
  const finishColor = data.exterior_finish_color?.trim() || '';
  const brand = data.exterior_finish_brand?.trim() || '';
  const style = data.exterior_finish_style?.trim() || '';

  if (finishType === 'paint' && !factoryColor) {
    return null;
  }

  if (finishType === 'paint') {
    return {
      finishType,
      hasWrapOrPpf: false,
      badgeLabel: null,
      primaryText: factoryColor,
      secondaryText: 'Factory paint',
      swatchLabel: factoryColor,
    };
  }

  const badgeLabel = finishType === 'wrap' ? 'Wrap' : 'PPF';
  const finishParts = [finishColor, style].filter(Boolean);
  const primaryText = finishParts.length > 0
    ? finishParts.join(' · ')
    : finishType === 'wrap'
      ? 'Vinyl wrap'
      : 'Paint protection film';

  const detailParts = [brand].filter(Boolean);
  const secondaryText = factoryColor
    ? `Factory: ${factoryColor}`
    : undefined;

  return {
    finishType,
    hasWrapOrPpf: true,
    badgeLabel,
    primaryText,
    secondaryText,
    detailText: detailParts.length > 0 ? detailParts.join(' · ') : undefined,
    swatchLabel: finishColor || factoryColor || undefined,
  };
}

export function exteriorFinishToPayload(data: {
  exterior_finish_type: ExteriorFinishType;
  exterior_finish_color: string;
  exterior_finish_brand: string;
  exterior_finish_style: string;
}) {
  return {
    exterior_finish_type: data.exterior_finish_type,
    exterior_finish_color: data.exterior_finish_color.trim() || undefined,
    exterior_finish_brand: data.exterior_finish_brand.trim() || undefined,
    exterior_finish_style: data.exterior_finish_style.trim() || undefined,
  };
}

export function validateExteriorFinishInput(
  finishType: ExteriorFinishType,
  finishColor: string
): string | null {
  if (finishType !== 'paint' && !finishColor.trim()) {
    return finishType === 'wrap'
      ? 'Enter the wrap color or name (e.g. Satin Nardo Grey)'
      : 'Enter the PPF finish (e.g. Clear, Stealth, or color name)';
  }
  return null;
}

/** Best-effort CSS color for circular swatches — named colors only. */
export function colorNameToCss(color?: string | null): string | undefined {
  if (!color) return undefined;
  const normalized = color.trim().toLowerCase();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalized)) {
    return normalized;
  }
  if (/^(rgb|hsl)\(/i.test(normalized)) {
    return color.trim();
  }
  return normalized;
}
